import React, { useEffect, useState } from "react";
import {Container, MenuItem} from "@mui/material";
import styles from "../PostJobPage/PostJob.module.scss";
import shiftBoxStyles from "../../../components/Modal/components/ModalShiftDescriptionContainer/ModalShiftDescriptionContainer.module.scss";
import {
    timePickerOptions,
    isBelowMinShiftDuration,
    MIN_SHIFT_DURATION_ERROR,
    isFirstPatientTimeBeforeStart,
    isFirstPatientTimeEqualToStart,
    FIRST_PATIENT_BUFFER_CAUTION,
    FIRST_PATIENT_BEFORE_START_ERROR,
    JOB_STATUSES,
} from "../PostJobPage/PostJob.utils";
import clsx from "clsx";
import {FormMaterialSelector} from "components/Selector";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {useForm, useWatch} from "react-hook-form";
import {joiResolver} from "@hookform/resolvers/joi";
import {JobProps} from "utils/types";
import {useHistory} from "react-router";
import dayjs from "dayjs";
import {TopBarRedesigned} from "../../../components/TopBarRedesigned";
import {HeaderDescription, Value} from "../ConfirmedOfferPage/ConfirmedOfferPage.styles";
import {StyledButton} from "components/Buttons/DefaultButton/DefaultButton.styles";
import useActionApi from "../../../hooks/useActionApi";
import Api from "../../../api";
import FixedLoader from "../../../components/FixedLoader";
import joi from "joi";
import {SimpleModal} from "../../../components/Modal";
import ModalTitleContainer from "../../../components/Modal/components/ModalTitleContainer";
import DateRangeOutlinedIcon from "@material-ui/icons/DateRangeOutlined";
import ScheduleOutlinedIcon from "@material-ui/icons/ScheduleOutlined";
import { ShiftStartedModal } from "../ConfirmedOfferPage/components/Modals";

export const createModifyJobSchema = (isBooked = false) => joi
    .object({
        from: joi.string().custom((value: string, helper: any) => {
            const date = new Date(value);
            if (!date || isNaN(date.getTime())) {
                return helper.message('From time is required and must be valid.');
            }
            return value;
        }).label('From').required(),
        to: joi.string().custom((value: string, helper: any) => {
            const date = new Date(value);
            const fromDate = new Date(helper.state.ancestors[0].from);
            if (!date || isNaN(date.getTime())) {
                return helper.message('To time is required and must be valid.');
            }
            if (date <= fromDate) {
                return helper.message('End time must be later than start time.');
            }
            if (isBelowMinShiftDuration(fromDate, date)) {
                return helper.message(MIN_SHIFT_DURATION_ERROR);
            }
            return value;
        }).label('To').required(),
        // T2-5444: optional; cannot be before start when editable
        firstPatientTime: joi.string().allow('').optional().custom((value: string, helper: any) => {
            // T2-7102: a booked shift keeps the original first-patient time in
            // form state for display, but the field is read-only and the API
            // deliberately synchronizes it to the new arrival time. Hidden
            // state must therefore never block the confirmation dialog.
            if (isBooked) return value;
            if (!value) return value;
            const from = helper.state.ancestors[0]?.from;
            if (from && isFirstPatientTimeBeforeStart(value, from)) {
                return helper.message(FIRST_PATIENT_BEFORE_START_ERROR);
            }
            return value;
        }),
    })
    .required()

export const schema = createModifyJobSchema();

const ACCEPTED_OFFER_STATUS = 2;

export const getModifyOfferNotice = (job: JobProps): string | null => {
    if (!job.partialOffers || job.partialOffers.length === 0) return null;

    const acceptedOffers = job.partialOffers.filter(({ status }) => (
        Number(status) === ACCEPTED_OFFER_STATUS
    ));
    const isPendingInstaBook = Number(job.status) === JOB_STATUSES.PARTIAL
        && Number(job.Autobook || 0) === 1
        && Number(job.hygienistId || 0) > 0
        && acceptedOffers.length === 1;

    if (isPendingInstaBook) {
        return 'Your pending Insta-Book request will stay assigned to this talent. We will notify them about the updated hours.';
    }

    return 'Please note that all offers will be automatically removed if you change the hours of this shift. We will notify the talent and ask them to submit a new offer if the updated hours still work for them.';
};

export const shouldBlockStartedShiftModification = ({
    status,
    startDate,
    postedStart,
    now = new Date(),
}: {
    status?: number | string | null;
    startDate?: string | null;
    postedStart?: string | null;
    now?: Date;
}): boolean => {
    if (Number(status) !== Number(JOB_STATUSES.CONFIRMED) || !startDate || !postedStart) {
        return false;
    }
    const start = dayjs(`${String(startDate).replace(/-/g, '/')} ${postedStart}`);
    return start.isValid() && !start.isAfter(dayjs(now));
};

const ModifyShiftModal = ({ from, to, onSubmit, onCloseButtonClick, job}: {  from: Date, to: Date, onSubmit: (e: any) => void, onCloseButtonClick: (e: any) => void, job: JobProps }) => {
    const offerNotice = getModifyOfferNotice(job);
    return (
        <>
            <ModalTitleContainer title="Modify Shift?" onClose={onCloseButtonClick} />
            
            <div className={shiftBoxStyles.shiftDescriptionBox}>
                <div className={shiftBoxStyles.shiftBoxDate__modifyShift}>
                    <div className={shiftBoxStyles.date}>
                        <DateRangeOutlinedIcon className={shiftBoxStyles.shiftDateIcon}/>
                        {dayjs(from).format('dddd, MMM D, YYYY')}
                    </div>
                    <div className={shiftBoxStyles.time}>
                        <ScheduleOutlinedIcon className={shiftBoxStyles.shiftDateIcon}/>
                        {`${dayjs(from).format('h:mm a')} - ${dayjs(to).format('h:mm a')}`}
                    </div>
                </div>
            </div>
            
            {offerNotice && (
                <div className={styles.description}>
                    {offerNotice}
                </div>
            )}
            
            <StyledButton
                variant="outlined"
                size="large"
                onClick={onCloseButtonClick}
                style={{ marginBottom: '1rem' }}
            >
                Cancel
            </StyledButton>
            <StyledButton
                variant="contained"
                size="large"
                onClick={onSubmit}
                className={clsx("bigButtonInNewStyle", "no-fastclick")}
            >
                Ok
            </StyledButton>
        </>
    );
}

const ModifyJobPage = () => {
    const history = useHistory();
    const { job } = history.location.state as { job: JobProps }
    const shift = job.shifts[0]
    const [isModifyModalOpen, setIsModifyModalOpen] = React.useState(false);
    const [isShiftStartedModalOpen, setIsShiftStartedModalOpen] = useState(false);
    const isBooked = job.status === JOB_STATUSES.CONFIRMED || job.status === JOB_STATUSES.COMPLETED;
    const validationSchema = React.useMemo(() => createModifyJobSchema(isBooked), [isBooked]);

    // T2-6106 / T2-7264: the started guard applies only to a booked shift.
    // Posted and Partial shifts remain editable regardless of their original start.
    useEffect(() => {
        if (shouldBlockStartedShiftModification({
            status: job?.status,
            startDate: job?.startDate,
            postedStart: shift?.postedStart,
        })) {
            setIsShiftStartedModalOpen(true);
        }
    }, [job?.status, job?.startDate, shift?.postedStart]);

    const [modifyJobLoading, , modifyJob] = useActionApi({
        apiMethod: Api.modifyJob,
        onSuccess: () => {
            history.goBack()
        },
    });

    const handleShiftStartedDismiss = () => {
        setIsShiftStartedModalOpen(false);
        history.goBack();
    };

    // Create Date objects for the form logic and modal display
    const defaultFromDate = dayjs(shift.shiftDate + ' ' + shift.postedStart, 'YYYY-MM-DD HH:mm a').toDate();
    const defaultToDate = dayjs(shift.shiftDate + ' ' + shift.postedEnd, 'YYYY-MM-DD HH:mm a').toDate();
    const defaultFirstPatientDate = shift.firstPatientTime
        ? dayjs(shift.shiftDate + ' ' + shift.firstPatientTime, 'YYYY-MM-DD HH:mm a').toDate()
        : null;

    const {
        control,
        handleSubmit,
        getValues,
        formState: { errors },
    } = useForm({
        defaultValues: {
            // Convert to string format to match timePickerOptions MenuItem values
            from: defaultFromDate.toString(),
            to: defaultToDate.toString(),
            firstPatientTime: defaultFirstPatientDate ? defaultFirstPatientDate.toString() : '',
        } as any,
        resolver: joiResolver(validationSchema) as any,
    });

    const firstPatientTime = useWatch({ control, name: 'firstPatientTime' });
    const fromWatch = useWatch({ control, name: 'from' });

    const onSubmit = async (data: any) => {
        setIsModifyModalOpen(false)
        // Convert string values back to Date objects for processing
        const postedStart = dayjs(new Date(data.from)).format('YYYY-MM-DD HH:mm')
        const postedEnd = dayjs(new Date(data.to)).format('YYYY-MM-DD HH:mm')
        const payload: {
            jobId: number;
            postedEnd: string;
            postedStart: string;
            firstPatientTime?: string | null;
        } = { jobId: job.id, postedEnd, postedStart };
        // T2-5444: only send firstPatientTime while unfilled; booked syncs server-side on start change
        if (!isBooked) {
            payload.firstPatientTime = data.firstPatientTime
                ? dayjs(new Date(data.firstPatientTime)).format('YYYY-MM-DD HH:mm')
                : null;
        }
        modifyJob(payload)
    }

    // Safely get form values and convert to Date objects for modal display
    const getFormValues = () => {
        const values = getValues();
        const from = values.from ? new Date(values.from) : defaultFromDate;
        const to = values.to ? new Date(values.to) : defaultToDate;
        return { from, to };
    }

    // Format selected value to show only time (e.g., "06:00 AM")
    const formatTimeDisplay = (selected: string) => {
        if (!selected) return '';
        const date = new Date(selected);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
    }

    const timeOptsArr = timePickerOptions(dayjs(shift.shiftDate)?.toDate(), 5, 23, 15)

    return (<>
        <TopBarRedesigned
            title={'Modify Shift'}
            onBack={() => {
                history.goBack()
            }}
        />

        <SimpleModal isOpen={isModifyModalOpen}>
            <ModifyShiftModal
                onSubmit={handleSubmit(onSubmit)}
                onCloseButtonClick={() => setIsModifyModalOpen(false)}
                from={getFormValues().from}
                to={getFormValues().to}
                job={job}
            />
        </SimpleModal>

        <ShiftStartedModal
            isOpen={isShiftStartedModalOpen}
            onClose={handleShiftStartedDismiss}
        />

        <Container className={styles.container}>
            <HeaderDescription>
                Edit Shift times
            </HeaderDescription>

                <Value style={{marginBottom: '1rem'}}>Shift Date: {dayjs(shift.shiftDate).format('dddd, MMM D')}</Value>

                <div className={clsx(styles.inputContainer, styles.timePickers)}>
                    <div className={styles.timePickerBox}>
                        <FormMaterialSelector 
                            control={control} 
                            name={'from'} 
                            label={'Arrival Time'}
                            formatSelectedValue={formatTimeDisplay}
                        >
                            {[
                                <MenuItem key={'selectArrivalTime'} value={''}>Select Arrival Time</MenuItem>,
                                ...timeOptsArr
                            ]}
                        </FormMaterialSelector>
                        <div className={styles.helperText}>
                            This sets the hourly rate range a temp sees when submitting their offer.
                        </div>
                    </div>
                    <div className={styles.timePickerBox}>
                        <FormMaterialSelector 
                            control={control} 
                            name={'to'} 
                            label={'End Time'}
                            formatSelectedValue={formatTimeDisplay}
                        >
                            {[
                                <MenuItem key={'selectEndingTime'} value={''}>Select Ending Time</MenuItem>,
                                ...timeOptsArr
                            ]}
                        </FormMaterialSelector>
                        <div className={styles.helperText}>
                            "End Time" is the time the shift is expected to end - considering last patient, clean-up
                            time and clinical notes.
                        </div>
                    </div>
                </div>

                {!isBooked ? (
                    <div
                        className={styles.inputContainer}
                        id="modify-job-first-patient-time"
                        data-testid="modify-job-first-patient-time"
                    >
                        <FormMaterialSelector
                            control={control}
                            name={'firstPatientTime'}
                            label={'First patient time'}
                            formatSelectedValue={formatTimeDisplay}
                        >
                            {[
                                <MenuItem key={'clearFirstPatientTime'} value={''}>Clear / Optional</MenuItem>,
                                ...timeOptsArr
                            ]}
                        </FormMaterialSelector>
                        <div className={styles.helperText}>
                            Optional. When the first patient is scheduled (separate from arrival).
                        </div>
                        {firstPatientTime && fromWatch && isFirstPatientTimeEqualToStart(firstPatientTime, fromWatch) && (
                            <div
                                className={styles.errorText}
                                data-testid="modify-job-first-patient-buffer-caution"
                            >
                                {FIRST_PATIENT_BUFFER_CAUTION}
                            </div>
                        )}
                        {errors?.firstPatientTime?.message && (
                            <div className={styles.errorText}>{String(errors.firstPatientTime.message)}</div>
                        )}
                    </div>
                ) : shift.firstPatientTime ? (
                    <Value style={{ marginBottom: '1rem' }}>
                        First patient time: {shift.firstPatientTime}
                        <div className={styles.helperText}>
                            Not editable after booking. Changing arrival time resets this to the new start.
                        </div>
                    </Value>
                ) : null}

                <div className={styles.bottomContainer}>
                    <StyledButton
                        variant="contained"
                        endIcon={<ArrowForwardIcon/>}
                        color="primary"
                        size="large"
                        id="continue-button"
                        onClick={handleSubmit(() => setIsModifyModalOpen(true))}
                    >
                        {`Confirm Change`}
                    </StyledButton>
                </div>
        </Container>
        <FixedLoader loading={modifyJobLoading}/>
    </>)
}

export default ModifyJobPage;
